/**
 * View Context Store (replaces mapContext)
 * Single source of truth for current view state and venue data
 */

import { defineStore } from "pinia";
import { ref, computed, markRaw, watch } from "vue";
import { useVenueDataStore } from "./venueData";
import {
    isEndUserHydrated,
    isEndUserVisible,
} from "@/helpers/endUserVisibility";
import { useStaticDataStore } from "./staticData";
import { IncidentSeverityOrder } from "@/constants.js";
import type {
    Venue,
    Level,
    Building,
    Space,
    EdgeDevice,
    EdgeUser,
    Marker,
    Safezone,
    Incident,
    IncidentComment,
} from "@/types/api";

// ================================================================
// Types (re-exported for backward compatibility)
// ================================================================

export interface MapContext {
    venueId: number | null;
    levelId: number | null;
    incidentId: number | null;
}

/**
 * Aggregate head-count for one space or building: how many visible people are
 * inside it, and the worst exposure level among them (which drives the colour
 * of the zoomed-out total marker).
 */
export interface EndUserTotal {
    count: number;
    worstExposureLevel: number | undefined;
}

export type LayerName =
    | "device-camera"
    | "device-tracking_beacon"
    | "device-master_beacon"
    | "device-door"
    | "device-gunshot_detector"
    | "device-first_aid_kit"
    | "device-bleed_control_kit"
    | "device-defibrillator"
    | "device-choking_rescue_device"
    | "devices"
    | "dispatchers"
    | "markers"
    | "endusers"
    | "enduser-safe"
    | "enduser-hazardous"
    | "enduser-critical"
    | "enduser-major"
    | "enduser-high"
    | "enduser-offsite"
    | "safezones";

// ================================================================
// Store
// ================================================================

export const useViewContextStore = defineStore("viewContext", () => {
    const venueDataStore = useVenueDataStore();
    const staticDataStore = useStaticDataStore();

    // ============================================================
    // STATE - Context
    // ============================================================

    const venueId = ref<number | null>(null);
    const levelId = ref<number | null>(null);
    const incidentId = ref<number | null>(null);
    const mode = ref<"operator" | "installer">("operator");

    // Route helpers (computed from mode/venueId)
    const modePrefix = computed(() => (mode.value === "installer" ? "i" : "o"));
    const homeRouteName = computed(() =>
        mode.value === "installer" ? "installer-home" : "operator-home",
    );
    const homeLink = computed(() =>
        venueId.value ? `/${modePrefix.value}/v/${venueId.value}` : "/",
    );
    const isInstallerMode = computed(() => mode.value === "installer");
    const isOperatorMode = computed(() => mode.value === "operator");

    // ============================================================
    // STATE - Selections
    // ============================================================

    const selectedDeviceId = ref<string | null>(null);
    const selectedEndUserId = ref<number | null>(null);
    const selectedMarkerId = ref<number | null>(null);
    const selectedCameraIds = ref<string[]>([]);
    const selectedEndUserIds = ref<number[]>([]);

    // ============================================================
    // STATE - Layers
    // ============================================================

    const showLayers = ref<LayerName[]>([
        "device-camera",
        "dispatchers",
        "markers",
    ]);

    // When true, map/widgets show objects from all levels (dimmed if off-level).
    // When false, only the currently selected level's objects are shown.
    const showAllLevels = ref(true);

    // Which incident's cameras the HOME camera widget is showing.
    //
    // Lives in the store rather than the widget because App.vue keys
    // <router-view> on $route.fullPath, so MapLayout fully remounts on every
    // navigation. Component state cannot survive incident -> HOME, which is
    // exactly the transition this value exists to bridge: an operator who was
    // viewing the shooter incident must still see its cameras after going HOME.
    const homeCameraIncidentId = ref<number | null>(null);

    // Camera widget search, stored here for the same remount reason.
    const cameraSearchPattern = ref("");

    // Camera auto-play, here for the remount reason. Incidents turn it on unless
    // the operator turned it off, until a venue switch, reload or new incident.
    // The venue's last incident resolving turns it off again.
    const cameraAutoPlay = ref(false);
    let cameraAutoPlayStoppedByUser = false;
    let autoPlayVenueId: number | null = null;
    const seenIncidentIds = new Set<number>();

    // ============================================================
    // COMPUTED - Data from VenueDataStore
    // ============================================================

    // Venue list (all venues, immediately available after init)
    const venues = computed<Venue[]>(() => venueDataStore.allVenuesList);

    // Current venue's levels
    const levels = computed<Level[]>(() =>
        venueId.value ? venueDataStore.getLevels(venueId.value) : [],
    );

    // Venue-wide data
    //
    // levelDataVersion is the reactivity bridge for markRaw level data.
    // These computeds aggregate data across ALL levels in the venue so that
    // off-level objects can be rendered dimmed on the map while remaining
    // interactive.
    //
    // Per-level ref caches restore Vue 3.4+ computed early-bailout: we compare
    // each level's array reference against the previous run and return the same
    // combined array when nothing changed. Without this, every levelDataVersion
    // bump (even a marker MQTT update) would create a new devices array and
    // trigger a full marker re-render, defeating the bailout entirely.

    let _devicesPerLevelRefs: EdgeDevice[][] = [];
    let _devicesForVenueCache: EdgeDevice[] = [];

    const getDevicesForVenue = computed<EdgeDevice[]>(() => {
        if (!venueId.value) return [];
        void venueDataStore.levelDataVersion;
        if (!showAllLevels.value && levelId.value) {
            return venueDataStore.getDevices(venueId.value, levelId.value);
        }
        const perLevel = levels.value.map((l) =>
            venueDataStore.getDevices(venueId.value!, l.id),
        );
        const unchanged =
            perLevel.length === _devicesPerLevelRefs.length &&
            perLevel.every((arr, i) => arr === _devicesPerLevelRefs[i]);
        if (unchanged) return _devicesForVenueCache;
        _devicesPerLevelRefs = perLevel;
        _devicesForVenueCache = perLevel.flat();
        return _devicesForVenueCache;
    });

    let _markersPerLevelRefs: Marker[][] = [];
    let _markersForVenueCache: Marker[] = [];

    const markersForVenue = computed<Marker[]>(() => {
        if (!venueId.value) return [];
        void venueDataStore.levelDataVersion;
        if (!showAllLevels.value && levelId.value) {
            return venueDataStore.getMarkers(venueId.value, levelId.value);
        }
        const perLevel = levels.value.map((l) =>
            venueDataStore.getMarkers(venueId.value!, l.id),
        );
        const unchanged =
            perLevel.length === _markersPerLevelRefs.length &&
            perLevel.every((arr, i) => arr === _markersPerLevelRefs[i]);
        if (unchanged) return _markersForVenueCache;
        _markersPerLevelRefs = perLevel;
        _markersForVenueCache = perLevel.flat();
        return _markersForVenueCache;
    });

    /**
     * Venue-wide master safezone list. Safezones are venue-scoped;
     * getSafezonesForVenue handles incident-based filtering.
     */
    const safezones = computed<Safezone[]>(() => {
        if (!venueId.value) return [];
        void venueDataStore.levelDataVersion;
        if (!showAllLevels.value && levelId.value) {
            return venueDataStore.getSafezones(venueId.value).filter(
                (sz) => sz.levelId === levelId.value,
            );
        }
        return venueDataStore.getSafezones(venueId.value);
    });

    // VenueData.endUsers is markRaw for MQTT-hot-path performance, so Vue can't
    // track in-place push/splice/Object.assign mutations. locationUpdateTick is
    // the reactivity bridge: applyDelta bumps it on every user batch, which
    // invalidates this computed the same way levelDataVersion bridges levelData
    // for spaces/buildings/markers/safezones/devices.
    const getEndUsersForVenue = computed<EdgeUser[]>(() => {
        if (!venueId.value) return [];
        void venueDataStore.locationUpdateTick;
        const users =
            !showAllLevels.value && levelId.value
                ? venueDataStore.getEndUsers(venueId.value, levelId.value)
                : venueDataStore.getAllEndUsersInVenue(venueId.value);
        // Hide MQTT stubs still awaiting fetchUserById — rendering them would
        // flash a wrong icon and inflate the overview count. The tick above
        // re-runs this computed the moment hydration lands, so they reappear.
        return users.filter(isEndUserHydrated);
    });

    // O(1) live-user lookup (any level of the venue, or tracked off-site) for per-row callers.
    const getLiveEndUser = (id: number): EdgeUser | undefined =>
        (venueId.value ? venueDataStore.getVenueEndUser(venueId.value, id) : undefined) ??
        venueDataStore.getOffsiteEndUser(id);

    // ------------------------------------------------------------
    // Derived end-user sets
    //
    // "Who is visible" and "how many people are in this space" are questions
    // about data, not about markers, so they are answered here rather than
    // inside the map marker helpers. Deriving them next to the marker pool
    // meant the personnel widget, the individual icons and the zoomed-out
    // totals each re-applied their own predicate, so the three could disagree
    // about the same people — and because the totals were only ever recomputed
    // while zoomed out, a helper that returned early left a stale head-count
    // painted on the map with nothing left to correct it.
    // ------------------------------------------------------------

    /**
     * The end-users that are renderable right now: exactly the set the map
     * draws icons for and the personnel widget lists. Every downstream count
     * starts here so they cannot drift apart.
     */
    const visibleEndUsersForVenue = computed<EdgeUser[]>(() => {
        const exposureStringFromLevel = (level: number | undefined) =>
            level != null
                ? staticDataStore.getExposureStringFromLevel(level)
                : "";
        return getEndUsersForVenue.value.filter((user) =>
            isEndUserVisible(user, {
                showLayers: showLayers.value,
                exposureStringFromLevel,
            }),
        );
    });

    /**
     * exposureLevel -> severity rank, where a LOWER rank is worse, because
     * IncidentSeverityOrder is written most-severe first (critical, major,
     * high, hazardous, unknown, safe, offsite).
     *
     * Both marker helpers used to build this with
     * `IncidentSeverityOrder.indexOf(type.value)`, but the constant holds
     * `{ value, level }` objects rather than bare strings, so that lookup
     * always returned -1. Every level ranked identically and the "worst
     * exposure" driving a total marker's colour was really just the first
     * exposure level encountered in the group. Matching on `value` fixes it.
     */
    const exposureSeverityRank = computed<Map<number, number>>(() => {
        const severityIndexByValue = new Map<string, number>(
            IncidentSeverityOrder.map(
                (entry: { value: string }, index: number) =>
                    [entry.value, index] as [string, number],
            ),
        );
        const ranks = new Map<number, number>();
        staticDataStore.getExposureLevelTypes?.forEach(
            (type: { level: number; value: string }) => {
                const index = severityIndexByValue.get(type.value);
                if (index != null) ranks.set(type.level, index);
            },
        );
        return ranks;
    });

    /**
     * Group the visible users by a location id, counting them and keeping the
     * worst exposure level in each group. Levels the static data doesn't know
     * about never win, and ties keep the first user seen.
     */
    function totalsByLocation(
        users: EdgeUser[],
        locationIdOf: (user: EdgeUser) => number | null | undefined,
    ): Map<number, EndUserTotal> {
        const ranks = exposureSeverityRank.value;
        // Lower is worse, so an unrecognised level sorts below everything.
        const rankOf = (level: number | undefined) =>
            level == null
                ? Number.MAX_SAFE_INTEGER
                : (ranks.get(level) ?? Number.MAX_SAFE_INTEGER);

        const totals = new Map<number, EndUserTotal>();
        for (const user of users) {
            const locationId = locationIdOf(user);
            if (locationId == null) continue;

            const total = totals.get(locationId);
            if (!total) {
                totals.set(locationId, {
                    count: 1,
                    worstExposureLevel: user.exposureLevel,
                });
                continue;
            }
            total.count += 1;
            if (rankOf(user.exposureLevel) < rankOf(total.worstExposureLevel)) {
                total.worstExposureLevel = user.exposureLevel;
            }
        }
        return totals;
    }

    /** Visible head-count per space, keyed by Space.id. */
    const endUserTotalsBySpace = computed<Map<number, EndUserTotal>>(() =>
        totalsByLocation(visibleEndUsersForVenue.value, (user) => user.spaceId),
    );

    /** Visible head-count per building, keyed by Building.id. */
    const endUserTotalsByBuilding = computed<Map<number, EndUserTotal>>(() =>
        totalsByLocation(
            visibleEndUsersForVenue.value,
            (user) => user.buildingId,
        ),
    );

    const activeIncidents = computed<Incident[]>(() =>
        venueId.value ? venueDataStore.getActiveIncidents(venueId.value) : [],
    );

    // Idempotent, so firing on every incident update is harmless. Venues also
    // change through setContext(), so the reset can't live in setVenueId().
    watch(
        () =>
            [
                venueId.value,
                activeIncidents.value.map((incident) => incident.id),
            ] as const,
        ([venue, ids], previous) => {
            const hadIncidents = (previous?.[1].length ?? 0) > 0;
            if (venue !== autoPlayVenueId) {
                autoPlayVenueId = venue;
                seenIncidentIds.clear();
                cameraAutoPlay.value = false;
                cameraAutoPlayStoppedByUser = false;
            }

            if (ids.some((id) => !seenIncidentIds.has(id))) {
                cameraAutoPlayStoppedByUser = false;
            }
            ids.forEach((id) => seenIncidentIds.add(id));

            if (ids.length > 0 && !cameraAutoPlayStoppedByUser) {
                cameraAutoPlay.value = true;
            } else if (ids.length === 0 && hadIncidents) {
                // Only on the last one resolving, so a manual switch-on at HOME
                // sticks. Running feeds are left up; the toggle doesn't stop them.
                cameraAutoPlay.value = false;
            }
        },
        { immediate: true },
    );

    // Comments for the current incident (if one is selected)
    const incidentComments = computed<IncidentComment[]>(() =>
        venueId.value && incidentId.value
            ? venueDataStore.getIncidentComments(
                  venueId.value,
                  incidentId.value,
              )
            : [],
    );

    // Level data structures (from current level)
    const spaces = computed<Space[]>(() => {
        if (!venueId.value || !levelId.value) return [];
        // Read levelDataVersion to force recomputation when fetchLevelData completes.
        // The levelData Map is markRaw (for MQTT performance), so Vue can't track
        // mutations to it directly — this counter acts as the reactivity signal.
        void venueDataStore.levelDataVersion;
        return (
            venueDataStore.getLevelData(venueId.value, levelId.value)?.spaces ??
            []
        );
    });

    const buildings = computed<Building[]>(() => {
        if (!venueId.value || !levelId.value) return [];
        // Read levelDataVersion to force recomputation when fetchLevelData completes.
        void venueDataStore.levelDataVersion;
        return (
            venueDataStore.getLevelData(venueId.value, levelId.value)
                ?.buildings ?? []
        );
    });

    // ============================================================
    // COMPUTED - Getters
    // ============================================================

    const context = computed<MapContext>(() => ({
        venueId: venueId.value,
        levelId: levelId.value,
        incidentId: incidentId.value,
    }));

    const getVenue = computed(() =>
        venueId.value ? venueDataStore.getVenue(venueId.value) : null,
    );

    const getLevel = computed(
        () => levels.value.find((l) => l.id === levelId.value) ?? null,
    );

    // Cache for enriched marker objects keyed by marker ID.
    // Avoids allocating a new {  ...marker, incident, incidentStatus } spread object
    // on every activeIncidents mutation (e.g. every MQTT incident update triggers a
    // full .map() creating N new objects, one per marker, all immediately GC'd).
    // The cache is markRaw so Vue never tries to make the Map itself reactive.
    type EnrichedMarkerCacheEntry = {
        markerStatus: string | undefined;
        incidentStatus: string;
        /** levelDataVersion at cache time — detects any in-place mutation */
        version: number;
        result: ReturnType<typeof _buildEnrichedMarker>;
    };
    const _enrichedMarkerCache = markRaw(new Map<number, EnrichedMarkerCacheEntry>());

    function _buildEnrichedMarker(
        marker: Marker,
        incident: Incident | undefined,
    ) {
        return {
            ...marker,
            incident: incident ?? null,
            incidentStatus: incident?.status ?? marker.status ?? "confirmed",
        };
    }

    // Promoting an SOS keeps the incident but changes its type, and the backend
    // leaves the original `sos` marker in place alongside the marker it creates
    // for the target type — so the map ends up showing two markers, the stale
    // one still labelled with the old SOS title, for a single incident.
    // An `sos` marker only means anything while the incident is still an SOS,
    // so once the type has moved on the marker is stale by definition.
    // TODO: drop this once promote retires the SOS marker backend-side.
    function _isStaleSosMarker(marker: Marker): boolean {
        if (marker.type !== "sos") return false;
        const incident = activeIncidents.value.find(
            (i) => i.id === marker.incidentId,
        );
        return !!incident && incident.type !== "sos";
    }

    // Extracted enrichment logic to be shared by both level and venue getters
    function enrichMarkers(sourceMarkers: Marker[]) {
        const scopedMarkers = incidentId.value
            ? sourceMarkers.filter((m) => m.incidentId === incidentId.value)
            : sourceMarkers;
        const filteredMarkers = scopedMarkers.filter(
            (m) => !_isStaleSosMarker(m),
        );

        return filteredMarkers.map((marker) => {
            const incident = activeIncidents.value.find(
                (i) => i.id === marker.incidentId,
            );
            const incidentStatus =
                incident?.status ?? marker.status ?? "confirmed";

            const cached = _enrichedMarkerCache.get(marker.id);
            const currentVersion = venueDataStore.levelDataVersion;
            if (
                cached &&
                cached.version === currentVersion &&
                cached.markerStatus === marker.status &&
                cached.incidentStatus === incidentStatus
            ) {
                return cached.result;
            }

            const result = _buildEnrichedMarker(marker, incident);
            _enrichedMarkerCache.set(marker.id, {
                markerStatus: marker.status,
                incidentStatus,
                version: currentVersion,
                result,
            });
            return result;
        });
    }

    const getMarkersForVenue = computed(() => {
        return enrichMarkers(markersForVenue.value);
    });

    const getSafezonesForVenue = computed(() => {
        // Single-incident (dashboard) path: show only that incident's
        // selected safezones. Unaffected by CV-1619.
        if (incidentId.value) {
            const incident = activeIncidents.value.find(
                (i) => i.id === incidentId.value,
            );
            if (!incident?.incidentSafeZones) return [];
            const assignedSafezoneIds = new Set<number>();
            for (const isz of incident.incidentSafeZones) {
                const id = isz.safezone?.id ?? isz.safeZoneId;
                if (id != null) assignedSafezoneIds.add(id);
            }
            return safezones.value.filter((sz) =>
                assignedSafezoneIds.has(sz.id),
            );
        }

        // HOME view with running incidents (CV-1619): show only the union of
        // safezones selected across ALL currently-running incidents.
        if (activeIncidents.value.length > 0) {
            const assignedSafezoneIds = new Set<number>();
            for (const incident of activeIncidents.value) {
                if (!incident.incidentSafeZones) continue;
                for (const isz of incident.incidentSafeZones) {
                    const id = isz.safezone?.id ?? isz.safeZoneId;
                    if (id != null) assignedSafezoneIds.add(id);
                }
            }
            return safezones.value.filter((sz) =>
                assignedSafezoneIds.has(sz.id),
            );
        }

        // No active incidents: show all safezones (default).
        return safezones.value;
    });

    const getActiveIncidentsForVenue = computed(() =>
        venueId.value
            ? activeIncidents.value.filter((i) => i.venueId === venueId.value)
            : [],
    );

    // Get current incident (for incident dashboard view)
    const currentIncident = computed<Incident | null>(() =>
        incidentId.value
            ? (activeIncidents.value.find((i) => i.id === incidentId.value) ??
              null)
            : null,
    );

    // Loading state
    const loading = computed(() => {
        if (!venueId.value) {
            return {
                venues: false,
                levels: false,
                devices: false,
                markers: false,
                spaces: false,
                buildings: false,
                safezones: false,
                endUsers: false,
                activeIncidents: false,
            };
        }
        const vd = venueDataStore.getVenueData(venueId.value);
        // Read levelDataVersion to force recomputation when fetchLevelData updates loading flags.
        // levelData is markRaw so Vue can't track the Map's mutations directly.
        void venueDataStore.levelDataVersion;
        const ld = levelId.value && vd ? vd.levelData.get(levelId.value) : null;
        return {
            venues: vd?.loading.venue ?? false,
            levels: vd?.loading.levels ?? false,
            devices: ld?.loading.devices ?? false,
            markers: ld?.loading.markers ?? false,
            spaces: ld?.loading.spaces ?? false,
            buildings: ld?.loading.buildings ?? false,
            safezones: vd?.loading.safezones ?? false,
            endUsers: vd?.loading.users ?? false,
            activeIncidents: vd?.loading.incidents ?? false,
        };
    });

    const isLoading = computed(() =>
        Object.values(loading.value).some(Boolean),
    );

    // ============================================================
    // ACTIONS - Context
    // ============================================================

    function setVenueId(id: number | null) {
        if (venueId.value !== id) {
            // Clear the enriched-marker cache so entries from the previous
            // venue don't accumulate (marker IDs could collide across venues).
            _enrichedMarkerCache.clear();

            // Release user-related caches from the previous venue. Users for
            // the new venue are re-fetched via fetchVenueUsers; without this,
            // _userObjectCache grows unbounded across venue switches during a
            // multi-hour operator shift.
            venueDataStore.trimUserCaches();

            // Incident ids are venue-scoped; carrying a selection across venues
            // would show the new venue an incident it does not have.
            homeCameraIncidentId.value = null;
        }
        venueId.value = id;
    }

    function setLevelId(id: number | null) {
        levelId.value = id;
    }

    function setIncidentId(id: number | null) {
        incidentId.value = id;
    }

    function setHomeCameraIncidentId(id: number | null) {
        homeCameraIncidentId.value = id;
    }

    function setCameraSearchPattern(pattern: string) {
        cameraSearchPattern.value = pattern;
    }

    /** The operator's toggle. Switching off stops incidents re-enabling it. */
    function setCameraAutoPlay(on: boolean) {
        cameraAutoPlay.value = on;
        cameraAutoPlayStoppedByUser = !on;
    }

    function setMode(newMode: "operator" | "installer") {
        mode.value = newMode;
    }

    function setContext({
        newVenueId,
        newLevelId,
        newIncidentId,
        newMode,
    }: {
        newVenueId?: number | null;
        newLevelId?: number | null;
        newIncidentId?: number | null;
        newMode?: "operator" | "installer";
    }) {
        if (newVenueId !== undefined) venueId.value = newVenueId;
        if (newLevelId !== undefined) levelId.value = newLevelId;
        if (newIncidentId !== undefined) incidentId.value = newIncidentId;
        if (newMode !== undefined) mode.value = newMode;
    }

    function setDefaultVenueAndLevel(venueList: Venue[], levelList: Level[]) {
        if (venueId.value === null && venueList?.length > 0) {
            const defaultVenue =
                venueList.find((v) => v.defaultVenue) || venueList[0];
            venueId.value = defaultVenue.id;

            if (levelId.value === null && levelList?.length > 0) {
                const venueLevels = levelList.filter(
                    (l) => l.venueId === defaultVenue.id,
                );
                const defaultLevel =
                    venueLevels.find((l) => l.defaultLevel) || venueLevels[0];
                if (defaultLevel) {
                    levelId.value = defaultLevel.id;
                }
            }
        }
    }

    function clearSelections() {
        incidentId.value = null;
        selectedDeviceId.value = null;
        selectedEndUserId.value = null;
        selectedMarkerId.value = null;
        selectedCameraIds.value = [];
        selectedEndUserIds.value = [];
    }


    // ============================================================
    // ACTIONS - Layers
    // ============================================================

    function setShowLayers(layers: LayerName[]) {
        showLayers.value = layers;
    }

    function toggleLayer(layerName: LayerName) {
        const idx = showLayers.value.indexOf(layerName);
        if (idx > -1) {
            showLayers.value.splice(idx, 1);
        } else {
            showLayers.value.push(layerName);
        }
    }

    function showLayer(layerName: LayerName) {
        if (!showLayers.value.includes(layerName)) {
            showLayers.value.push(layerName);
        }
    }

    function hideLayer(layerName: LayerName) {
        const idx = showLayers.value.indexOf(layerName);
        if (idx > -1) {
            showLayers.value.splice(idx, 1);
        }
    }

    // ============================================================
    // ACTIONS - Data Fetching (delegates to venueDataStore)
    // ============================================================

    async function fetchVenues() {
        if (venueId.value) {
            await venueDataStore.fetchVenueBasics(venueId.value);
        }
    }

    async function fetchLevels(targetVenueId: number) {
        await venueDataStore.fetchVenueBasics(targetVenueId);
        // Auto-select level
        const fetchedLevels = venueDataStore.getLevels(targetVenueId);
        if (fetchedLevels.length > 0 && !levelId.value) {
            const defaultLevel =
                fetchedLevels.find((l) => l.defaultLevel) || fetchedLevels[0];
            levelId.value = defaultLevel.id;
        }
    }

    async function fetchActiveIncidents(targetVenueId: number) {
        await venueDataStore.fetchActiveIncidents(targetVenueId);
    }

    async function fetchDevices(targetVenueId: number, targetLevelId: number) {
        await venueDataStore.fetchLevelData(targetVenueId, targetLevelId);
    }

    async function fetchMarkers(
        targetVenueId: number,
        targetLevelId: number,
        _targetIncidentId: number | null = null,
    ) {
        await venueDataStore.fetchLevelData(targetVenueId, targetLevelId);
    }

    async function fetchSpaces(targetVenueId: number, targetLevelId: number) {
        await venueDataStore.fetchLevelData(targetVenueId, targetLevelId);
    }

    async function fetchBuildings(
        targetVenueId: number,
        targetLevelId: number,
    ) {
        await venueDataStore.fetchLevelData(targetVenueId, targetLevelId);
    }

    async function fetchEndUsers(
        targetVenueId: number,
        targetLevelId: number,
        _targetIncidentId: number | null = null,
    ) {
        await venueDataStore.fetchLevelData(targetVenueId, targetLevelId);
    }

    async function fetchIncidentComments(targetIncidentId: number) {
        if (!venueId.value) return [];
        return venueDataStore.fetchIncidentComments(
            venueId.value,
            targetIncidentId,
        );
    }


    function $reset() {
        venueId.value = null;
        levelId.value = null;
        incidentId.value = null;
        clearSelections();
        showLayers.value = [
            "device-camera",
            "dispatchers",
            "markers",
        ];
        showAllLevels.value = true;
    }

    // ============================================================
    // RETURN
    // ============================================================

    return {
        // Context state
        venueId,
        levelId,
        incidentId,
        mode,
        // Route helpers
        modePrefix,
        homeRouteName,
        homeLink,
        isInstallerMode,
        isOperatorMode,
        // Selections
        selectedDeviceId,
        selectedEndUserId,
        selectedMarkerId,
        selectedCameraIds,
        selectedEndUserIds,
        showLayers,
        showAllLevels,
        homeCameraIncidentId,
        cameraSearchPattern,
        cameraAutoPlay,

        // Data (from venueDataStore)
        venues,
        levels,
        buildings,
        spaces,
        activeIncidents,
        incidentComments,

        // Loading
        loading,
        isLoading,

        // Getters
        context,
        getVenue,
        getLevel,

        getDevicesForVenue,
        getMarkersForVenue,
        getEndUsersForVenue,
        getLiveEndUser,
        visibleEndUsersForVenue,
        endUserTotalsBySpace,
        endUserTotalsByBuilding,
        getSafezonesForVenue,
        getActiveIncidentsForVenue,
        currentIncident,

        // Context actions
        setContext,
        setVenueId,
        setLevelId,
        setIncidentId,
        setHomeCameraIncidentId,
        setCameraSearchPattern,
        setCameraAutoPlay,
        setMode,
        setDefaultVenueAndLevel,
        clearSelections,
        clearMapContext: clearSelections, // Alias

        // Layer actions
        setShowLayers,
        toggleLayer,
        showLayer,
        hideLayer,

        // Fetch actions (delegate to venueDataStore)
        fetchVenues,
        fetchLevels,
        fetchDevices,
        fetchMarkers,
        fetchSpaces,
        fetchBuildings,
        fetchEndUsers,
        fetchIncidentComments,
        fetchActiveIncidents,

        $reset,
    };
});

export type ViewContextStore = ReturnType<typeof useViewContextStore>;
